/**
 * Attachment types that must not be uploaded. Everything else is accepted.
 *
 * Keep this list focused on executable packages, installers, system images,
 * shortcuts and macro-enabled Office files. Text source files remain valid
 * attachments even when their contents can be interpreted as a script.
 */
export const BLOCKED_ATTACHMENT_EXTENSIONS: readonly string[] = [
    // Executables and system binaries
    "exe", "dll", "com", "scr", "cpl", "ocx", "sys", "drv", "pif", "class", "jar",
    // Installers and application packages
    "msi", "msp", "mst", "msix", "msixbundle", "appx", "appxbundle", "apk", "ipa", "deb", "rpm", "dmg", "pkg",
    // Disk and virtual-machine images
    "iso", "img", "vhd", "vhdx",
    // System shortcuts and active content
    "lnk", "scf", "url", "reg", "hta", "chm", "vbs", "vbe", "wsf", "wsh", "sct",
    // Macro-enabled Office documents and add-ins
    "docm", "dotm", "xlsm", "xltm", "xlam", "pptm", "potm", "ppam", "ppsm", "sldm",
];

const BLOCKED_EXTENSIONS = new Set(BLOCKED_ATTACHMENT_EXTENSIONS);

/** MIME types that identify executable or installable content. */
export const BLOCKED_ATTACHMENT_MIME_TYPES: readonly string[] = [
    "application/java-archive",
    "application/vnd.android.package-archive",
    "application/vnd.microsoft.portable-executable",
    "application/x-apple-diskimage",
    "application/x-dosexec",
    "application/x-executable",
    "application/x-iso9660-image",
    "application/x-mach-binary",
    "application/x-ms-installer",
    "application/x-msdos-program",
    "application/x-msdownload",
    "application/x-sharedlib",
];

const BLOCKED_MIME_TYPES = new Set(BLOCKED_ATTACHMENT_MIME_TYPES);

/** Show every file in the picker; {@link isAllowedAttachmentFile} enforces the denylist. */
export const ATTACHMENT_ACCEPT = "*/*";

export const IMAGE_ATTACHMENT_ACCEPT = "image/*";

export function isImageAttachmentFile(mimeType: string | undefined | null): boolean
{
    return !!mimeType && mimeType.toLowerCase().startsWith("image/");
}

export function getFileExtension(fileName: string): string | null
{
    const lastDot = fileName.lastIndexOf(".");
    if (lastDot < 0 || lastDot === fileName.length - 1) return null;
    return fileName.slice(lastDot + 1).toLowerCase();
}

export function isAllowedAttachmentMime(
    mimeType: string | undefined | null,
): boolean
{
    if (!mimeType) return true;
    const type = mimeType.split(";", 1)[ 0 ].trim().toLowerCase();
    return !BLOCKED_MIME_TYPES.has(type);
}

export function isAllowedAttachmentFile(
    fileName: string,
    mimeType?: string | null,
): boolean
{
    const extension = getFileExtension(fileName);
    if (extension && BLOCKED_EXTENSIONS.has(extension)) return false;

    return isAllowedAttachmentMime(mimeType);
}
